import { firebaseLog, firebaseError } from '@/helpers/firebaseDebug'
import isNativeDevice from '@/helpers/isNativeDevie'
import { initializeApp, getApps } from 'firebase/app'
import {
  browserLocalPersistence,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  indexedDBLocalPersistence,
  initializeAuth,
  onAuthStateChanged,
  setPersistence,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut
} from 'firebase/auth'
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth'

let _app = null
let _auth = null
/** @type {Promise<void>|null} */
let _persistencePromise = null
/** @type {Promise<void>|null} */
let _nativeGoogleReady = null

function getFirebaseConfig () {
  return {
    apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
    authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
    appId: process.env.VUE_APP_FIREBASE_APP_ID
  }
}

function assertFirebaseEnv (cfg) {
  const missing = []
  if (!cfg.apiKey) missing.push('VUE_APP_FIREBASE_API_KEY')
  if (!cfg.authDomain) missing.push('VUE_APP_FIREBASE_AUTH_DOMAIN')
  if (!cfg.projectId) missing.push('VUE_APP_FIREBASE_PROJECT_ID')
  if (!cfg.appId) missing.push('VUE_APP_FIREBASE_APP_ID')
  if (missing.length) {
    throw new Error(`Faltan variables de entorno Firebase: ${missing.join(', ')}`)
  }
}

/**
 * Vue 2 / Vuex no deben guardar la instancia User de Firebase (reactividad rompe prototipos del SDK).
 */
export function toFirebaseUserSnapshot (user) {
  if (!user) return null
  return {
    uid: user.uid,
    email: user.email || null,
    displayName: user.displayName || null
  }
}

export function initFirebase () {
  if (_app && _auth) {
    return { app: _app, auth: _auth }
  }
  const cfg = getFirebaseConfig()
  assertFirebaseEnv(cfg)
  firebaseLog('initFirebase (nuevo)', {
    projectId: cfg.projectId,
    authDomain: cfg.authDomain,
    native: !!isNativeDevice(),
    location: typeof window !== 'undefined' ? window.location.href : '(ssr)'
  })
  if (getApps().length === 0) {
    _app = initializeApp(cfg)
  } else {
    _app = getApps()[0]
  }

  // En Capacitor/WebView Android, browserLocalPersistence puede colgarse.
  // indexedDBLocalPersistence es el recomendado para entornos nativos híbridos.
  if (isNativeDevice()) {
    try {
      _auth = initializeAuth(_app, { persistence: indexedDBLocalPersistence })
      _persistencePromise = Promise.resolve()
    } catch (e) {
      // Ya inicializado (HMR / reentrada)
      _auth = getAuth(_app)
      _persistencePromise = Promise.resolve()
      firebaseLog('initFirebase: initializeAuth reuse', { message: e && e.message })
    }
  } else {
    _auth = getAuth(_app)
    if (!_persistencePromise) {
      _persistencePromise = setPersistence(_auth, browserLocalPersistence).catch(() => {})
    }
  }
  return { app: _app, auth: _auth }
}

export async function ensureFirebasePersistence () {
  initFirebase()
  if (_persistencePromise) await _persistencePromise
}

/** Sincroniza sesión; `onAuthStateChanged` refleja mejor redirect/popup que solo `onIdTokenChanged`. */
export function watchFirebaseAuth (onChange) {
  const { auth } = initFirebase()
  return onAuthStateChanged(auth, async (user) => {
    try {
      const token = user ? await user.getIdToken() : null
      if (onChange) onChange({ user: toFirebaseUserSnapshot(user), token })
    } catch (e) {
      firebaseLog('onAuthStateChanged error', { code: e && e.code, message: e && e.message })
      if (onChange) {
        onChange({
          user: user ? toFirebaseUserSnapshot(user) : null,
          token: null,
          error: e
        })
      }
    }
  })
}

/**
 * Redirect guarda el “pending sign-in” por **origen** (host + puerto). Si `npm run serve`
 * pasa de :8080 a :8085 (u otro), `getRedirectResult` vuelve vacío y parece que “no loguea”.
 *
 * - Por defecto en **development** usamos **popup** (más estable con puertos variables).
 * - `VUE_APP_FIREBASE_FORCE_REDIRECT=TRUE` fuerza redirect (mismo puerto siempre).
 * - `VUE_APP_FIREBASE_FORCE_POPUP=TRUE` fuerza popup en cualquier entorno.
 */
export function shouldPreferFirebaseRedirect () {
  if (process.env.VUE_APP_FIREBASE_FORCE_REDIRECT === 'TRUE') return true
  if (process.env.VUE_APP_FIREBASE_FORCE_POPUP === 'TRUE') return false
  if (process.env.NODE_ENV === 'development') return false
  return false
}

export async function completeGoogleRedirectIfNeeded () {
  try {
    await ensureFirebasePersistence()
    // En Capacitor no usamos signInWithRedirect: getRedirectResult suele tirar
    // auth/argument-error con persistencia IndexedDB / estado viejo y asusta al usuario al abrir la app.
    if (isNativeDevice()) {
      firebaseLog('getRedirectResult: omitido (plataforma nativa)')
      return { result: null, error: null }
    }
    const { auth } = initFirebase()
    firebaseLog('getRedirectResult: antes', {
      href: typeof window !== 'undefined' ? window.location.href : '',
      currentUid: auth.currentUser && auth.currentUser.uid
    })
    const result = await getRedirectResult(auth)
    firebaseLog('getRedirectResult: después', {
      resultIsNullish: result == null,
      operationType: result && result.operationType,
      providerId: result && result.providerId,
      hasUser: !!(result && result.user),
      userUid: result && result.user && result.user.uid,
      userEmail: result && result.user && result.user.email,
      currentUid: auth.currentUser && auth.currentUser.uid
    })
    if (result == null || !(result && result.user)) {
      firebaseLog(
        'getRedirectResult: sin credencial de redirect (¿cambió el puerto u origen desde que tocaste “Continuar con Google”?)'
      )
    }
    return { result, error: null }
  } catch (e) {
    firebaseLog('getRedirectResult: excepción', { code: e && e.code, message: e && e.message })
    // Errores de restore (p. ej. argument-error) no son fallos de “login” del usuario.
    return { result: null, error: e, soft: true }
  }
}

function nativeGoogleClientId () {
  return (process.env.VUE_APP_GOOGLE_WEB_CLIENT_ID || '').trim()
}

function isNativeGoogleCancel (e) {
  const code = e && e.code != null ? String(e.code) : ''
  const msg = e && e.message ? String(e.message) : String(e || '')
  return (
    code === '12501' ||
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request' ||
    /12501|canceled|cancelled|popup_closed/i.test(msg)
  )
}

function nativeGoogleError (e) {
  const raw = `${(e && e.code) || ''} ${(e && e.message) || e || ''}`
  if (e && e.code === 'auth/timeout') return e
  if (/(\b10\b|DEVELOPER_ERROR)/i.test(raw)) {
    const err = new Error(
      'Google rechazó el login (error 10). En Firebase → Project settings → Tu app Android (app.clickaway) agregá SHA-1 y SHA-256 del keystore con el que firmás el APK/AAB (debug local y/o firma de Play Console → Integridad de la app). Esperá unos minutos, regenerá google-services.json si hace falta, e intentá de nuevo.'
    )
    err.code = 'auth/developer-error'
    return err
  }
  if (e && e.message) return e
  const err = new Error(String(e || 'No se pudo iniciar sesión con Google'))
  err.code = (e && e.code) || 'auth/unknown'
  return err
}

function withTimeout (promise, ms, message) {
  let timer = null
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const err = new Error(message)
      err.code = 'auth/timeout'
      reject(err)
    }, ms)
  })
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => {
    if (timer) clearTimeout(timer)
  })
}

async function ensureNativeGoogleAuthReady (clientId) {
  if (_nativeGoogleReady) {
    await _nativeGoogleReady
    return
  }
  _nativeGoogleReady = (async () => {
    if (typeof GoogleAuth.initialize === 'function') {
      await GoogleAuth.initialize({
        clientId,
        serverClientId: clientId,
        scopes: ['profile', 'email'],
        grantOfflineAccess: false
      })
    }
  })()
  try {
    await _nativeGoogleReady
  } catch (e) {
    _nativeGoogleReady = null
    throw e
  }
}

function extractGoogleIdToken (googleUser) {
  if (!googleUser) return null
  const auth = googleUser.authentication || {}
  return (
    auth.idToken ||
    auth.id_token ||
    googleUser.idToken ||
    googleUser.id_token ||
    null
  )
}

async function loginWithGoogleNative () {
  const clientId = nativeGoogleClientId()
  if (!clientId) {
    throw new Error(
      'Falta VUE_APP_GOOGLE_WEB_CLIENT_ID en .env (Firebase Console → Authentication → Sign-in method → Google → Web client ID).'
    )
  }

  await withTimeout(
    (async () => {
      await ensureFirebasePersistence()
      const { auth } = initFirebase()
      await ensureNativeGoogleAuthReady(clientId)

      // Evita sesión previa “a medias” que a veces no dispara el activity result.
      try {
        await GoogleAuth.signOut()
      } catch (e) {
        firebaseLog('GoogleAuth.signOut previo', { message: e && e.message })
      }

      firebaseLog('loginWithGoogleNative: signIn')
      const googleUser = await withTimeout(
        GoogleAuth.signIn(),
        90000,
        'Google no respondió a tiempo. Si cancelaste el diálogo, volvé a intentar. Si no apareció nada, revisá SHA-1/SHA-256 en Firebase.'
      )

      const idToken = extractGoogleIdToken(googleUser)
      if (!idToken) {
        throw new Error('Google no devolvió el token de sesión (idToken vacío)')
      }

      const credential = GoogleAuthProvider.credential(idToken)
      firebaseLog('loginWithGoogleNative: signInWithCredential')
      await withTimeout(
        signInWithCredential(auth, credential),
        20000,
        'Firebase no confirmó la sesión a tiempo. Revisá la conexión a internet y la configuración del proyecto Firebase.'
      )
      firebaseLog('loginWithGoogleNative: OK')
    })(),
    110000,
    'El inicio de sesión con Google se demoró demasiado y se canceló. Probá de nuevo.'
  )
}

export async function loginWithGoogle ({ preferRedirect } = {}) {
  // Nunca usar popup/redirect de Firebase dentro de WebView Capacitor: cuelga sin error.
  if (isNativeDevice()) {
    try {
      await loginWithGoogleNative()
    } catch (e) {
      if (isNativeGoogleCancel(e)) {
        const cancel = new Error('Inicio de sesión cancelado')
        cancel.code = 'auth/popup-closed-by-user'
        throw cancel
      }
      firebaseError('loginWithGoogleNative: error', {
        code: e && e.code,
        message: e && e.message,
        errorMessage: e && e.errorMessage
      })
      throw nativeGoogleError(e)
    }
    return
  }
  await ensureFirebasePersistence()
  const { auth } = initFirebase()
  const provider = new GoogleAuthProvider()
  provider.addScope('profile')
  provider.addScope('email')
  provider.setCustomParameters({ prompt: 'select_account' })
  const useRedirect = !!preferRedirect || shouldPreferFirebaseRedirect()
  firebaseLog('loginWithGoogle', { useRedirect, preferRedirect: !!preferRedirect })
  if (useRedirect) {
    firebaseLog('signInWithRedirect: iniciando (la página va a recargar al volver)')
    await signInWithRedirect(auth, provider)
    return
  }
  try {
    firebaseLog('signInWithPopup: iniciando')
    await signInWithPopup(auth, provider)
    firebaseLog('signInWithPopup: OK')
  } catch (e) {
    const code = e && e.code ? e.code : ''
    // No convertir "cerró ventana" en redirect: confunde UX y parece "no pasa nada".
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      throw e
    }
    // Fallback si popup está bloqueado / no soportado
    if (
      code === 'auth/popup-blocked' ||
      code === 'auth/operation-not-supported-in-this-environment'
    ) {
      firebaseLog('signInWithPopup falló, redirect fallback', { code: e && e.code })
      await signInWithRedirect(auth, provider)
      return
    }
    firebaseLog('signInWithPopup: error', { code: e && e.code, message: e && e.message })
    throw e
  }
}

export async function logout () {
  if (isNativeDevice()) {
    try {
      await GoogleAuth.signOut()
    } catch (e) {
      firebaseLog('GoogleAuth.signOut nativo', { message: e && e.message })
    }
  }
  const { auth } = initFirebase()
  await signOut(auth)
}
